// @ts-strict-ignore
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { DrillingHoleRange, DrillingHoleSize } from '@3d-scene/model3d/util/drillinghole/drilling-hole-size';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleUtils } from '@article/util/article-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { LocaleUtils } from '@shared/util/locale-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { DrillingholePresetVO } from '@project/actions/drilling-hole/drilling-hole.vo';

export class DrillingHoleUtils {
  /**
   * Return index of the given <code>drillingHoleInfo</code> in the source array. If such object is not found then -1 is returned.
   */
  public static findDrillingHole(drillingHoleInfo: DrillingHoleInfo, source: DrillingHoleInfo[]): number {
    if (!drillingHoleInfo || !source || source.length === 0) {
      return -1;
    }
    for (let i: number = 0; i < source.length; i++) {
      const sourceInfo: DrillingHoleInfo = source[i];
      if (drillingHoleInfo.equals(sourceInfo)) {
        return i;
      }
    }
    return -1;
  }

  public static findDrillingHoleSize(drillingHoleSize: DrillingHoleSize, source: DrillingHoleSize[]): number {
    if (!drillingHoleSize || !source || source.length === 0) {
      return -1;
    }
    for (let i: number = 0; i < source.length; i++) {
      const sourceSize: DrillingHoleSize = source[i];
      if (drillingHoleSize.equals(sourceSize)) {
        return i;
      }
    }
    return -1;
  }

  public static findArticleByDrillingHole(drillingHole: DrillingHoleInfo, articles: ArticleVO[]): ArticleVO {
    if (!drillingHole || !articles) {
      return null;
    }
    for (const article of articles) {
      const articleShapeType: string = this.getDrillingHoleShapeTypeByArticle(article);
      const articleSize: DrillingHoleSize = this.getDrillingHoleSizeByArticle(article);
      if (!drillingHole.size || !drillingHole.shapeType) {
        return null;
      }
      if (drillingHole.shapeType === articleShapeType && drillingHole.size.equals(articleSize)) {
        return article;
      }
    }
    return null;
  }

  public static validateDrillingHoles(drillingHoles: DrillingHoleInfo[], totalLength: number): boolean {
    if (!Array.isArray(drillingHoles)) {
      return false;
    }
    const drillingHolesLength: number = drillingHoles.length;
    if (drillingHolesLength > 0) {
      this.sortDrillingHolesByPosition(drillingHoles);
      for (let i: number = 0; i < drillingHolesLength - 1; i++) {
        if (this.isIntersectDrillingHoles(drillingHoles[i], drillingHoles[i + 1])) {
          return false;
        }
      }
    }
    return true;
  }

  private static isIntersectDrillingHoles(source: DrillingHoleInfo, target: DrillingHoleInfo): boolean {
    const distance: number = Math.abs(target.position - source.position);
    return (distance < (target.size.sizeOnLine / 2 + source.size.sizeOnLine / 2)) ? true : false;
  }

  public static getDrillingHoleShapeTypeByArticle(article: ArticleVO): string {
    return ArticleUtils.getArticleBehaviorPropertyStringValue(article, ArticleBehaviorPropertyDef.SHAPE_TYPE);
  }

  public static getDrillDimensionIDByArticle(article: ArticleVO): number {
    return ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.DIMENSION_ID);
  }

  public static getDrillingHoleSizeLabelByArticle(article: ArticleVO): string {
    return ArticleUtils.getArticleBehaviorPropertyStringValue(article, ArticleBehaviorPropertyDef.SIZE_LABEL);
  }

  public static getDrillingHoleSizeOnLineByArticle(article: ArticleVO): number {
    // search size as float typed value of property, if it is not found (float is NaN), then search in int typed values
    let value: number = ArticleUtils.getArticleBehaviorPropertyFloatValue(article, ArticleBehaviorPropertyDef.SIZE_ON_LINE);
    if (NumberUtils.isNotNumber(value)) {
      value = ArticleUtils.getArticleBehaviorPropertyIntValue(article, ArticleBehaviorPropertyDef.SIZE_ON_LINE);
    }
    return value;
  }

  public static getDrillingHoleWidthByArticle(article: ArticleVO): number {
    let value: number = ArticleUtils.getArticleBehaviorPropertyFloatValue(article, ArticleBehaviorPropertyDef.ARTICLE_WIDTH);
    if (NumberUtils.isNotNumber(value) || value === 0) {
      value = ArticleUtils.getArticleBehaviorPropertyIntValue(article, ArticleBehaviorPropertyDef.ARTICLE_WIDTH);
    }
    return value;
  }

  public static getDrillingHoleSizeByArticle(article: ArticleVO): DrillingHoleSize {
    return new DrillingHoleSize(
      this.getDrillingHoleSizeOnLineByArticle(article),
      this.getDrillingHoleWidthByArticle(article),
      this.getDrillingHoleSizeLabelByArticle(article));
  }

  public static getLocalizedShapeType(localResourceManager: LocaleResourceManager,holeInfo: DrillingHoleInfo): string {
    if (!holeInfo || !holeInfo.shapeType) {
      return;
    }
    return LocaleUtils.getLocalizedDrillingHoleType(localResourceManager, holeInfo.shapeType);
  }

  public static copyDrillingHoleArray(source: DrillingHoleInfo[]): DrillingHoleInfo[] {
    if (!source || source.length === 0) {
      return [];
    }
    const result: DrillingHoleInfo[] = [];
    for (const drillingHole of source) {
      result.push(drillingHole.clone());
    }
    return result;
  }

  public static sortDrillingHolesByPosition(drillingHoles: DrillingHoleInfo[]): DrillingHoleInfo[] {
    if (!drillingHoles) {
      return null;
    }
    drillingHoles.sort((a: DrillingHoleInfo, b: DrillingHoleInfo) => a.position - b.position);
    return drillingHoles;
  }

  public static getStartDrillingHolePosition(preset: DrillingholePresetVO): DrillingHoleRange {
    if (!preset) {
      return null;
    }

    return this.getStartDrillingHolePositionByArticle(preset.drillingholeArticle, preset.distance);
  }

  public static getStartDrillingHolePositionByArticle(drillingHoleArticle: ArticleVO, distance: number): DrillingHoleRange {
    if (!drillingHoleArticle || NumberUtils.isNotNumber(distance)) {
      return null;
    }

    const drillingHoleSize: DrillingHoleSize = this.getDrillingHoleSizeByArticle(drillingHoleArticle);
    const min: number = UnitConverter.fromTo('3d', 'mm', (drillingHoleSize.width / 2) - (drillingHoleSize.sizeOnLine / 2));
    const max: number = distance - UnitConverter.fromTo('3d', 'mm', drillingHoleSize.sizeOnLine / 2);
    return new DrillingHoleRange(MathUtils.roundNumberValue(min, 1), MathUtils.roundNumberValue(max, 1));
  }

  public static getMaxDrillingHolePositionOffsetOnRail(drillingHoleArticle: ArticleVO, inAwayUnits: boolean = false): number {
    if (!drillingHoleArticle) {
      return NaN;
    }
    const drillingHolesSize: DrillingHoleSize = this.getDrillingHoleSizeByArticle(drillingHoleArticle);
    const drillingHoleSizePosOnRailOffset: number = MathUtils.roundNumberValue(drillingHolesSize.sizeOnLine / 2, 1);
    if (inAwayUnits) {
      return drillingHoleSizePosOnRailOffset;
    } else {
      return MathUtils.roundNumberValue(UnitConverter.fromTo('3d', 'mm', drillingHoleSizePosOnRailOffset), 1);
    }
  }

  public static roundDrillingHolePosition(position: number): number {
    position = UnitConverter.fromTo('3d', 'mm', position);
    position = MathUtils.roundNumberValue(position, 1);
    position = UnitConverter.fromTo('mm', '3d', position);
    return position;
  }

  public static getProductionCountryIso2Values(article: ArticleVO): string[] {
    if (!article) {
      return [];
    }
    if (!PropertyUtils.containsProperty(article.behaviorProperties, ArticleBehaviorPropertyDef.AVAILABLE_IN_PRODUCTION_COUNTRY)) {
      return null;
    }
    return ArticleUtils.getArticleBehaviorPropertyStringValues(article, ArticleBehaviorPropertyDef.AVAILABLE_IN_PRODUCTION_COUNTRY);
  }

  public static getLocalizedSizeLabel(holeSize: DrillingHoleSize, localeManager: LocaleResourceManager): string {
    if (!localeManager || !holeSize || isNaN(holeSize.sizeOnLine)) {
      return '';
    }
    const holeLength: number = UnitConverter.fromTo('3d', 'mm', holeSize.sizeOnLine);
    let sizeLabel: string = localeManager.formatNumber(MathUtils.roundNumberValue(holeLength), false);
    if (!isNaN(holeSize.width)) {
      const holeWidth: number = UnitConverter.fromTo('3d', 'mm', holeSize.width);
      sizeLabel += DrillingHoleSize.SIZE_SEPARATOR + localeManager.formatNumber(MathUtils.roundNumberValue(holeWidth), false);
    }
    return sizeLabel;
  }
}
